import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { Video } from 'lucide-react';
import { isMetricSelected, getCosClassName, getCpaClassName } from '../utils/metricsUtils';

/**
 * Composant table modulaire des vidéos Performance Max
 * Utilise l'endpoint dédié pour récupérer les données vidéos avec métriques
 */
const PerformanceMaxVideosTable = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedVideoFilter,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  token,
  setError,
  onDataLoaded,
  apiEndpoint = '/api/dashboard/components/performance-max/videos',
  moduleId = 'pmax_videos'
}) => {
  // États locaux
  const [videosData, setVideosData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [metricsMode, setMetricsMode] = useState('performance'); // 'performance' ou 'engagement'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'manual', 'auto'
  
  // Configuration de pagination
  const itemsPerPage = 10;
  
  
  // Définition des métriques par mode
  const engagementMetrics = ['impressions', 'vtr', 'views', 'cpv', 'cpm', 'engagements', 'taux_engagement'];
  const performanceMetrics = ['impressions', 'clics', 'cout', 'ctr', 'cpc', 'conv', 'taux_conv', 'cpa', 'valeur_conv', 'cos'];
  
  // Fonction pour vérifier si une métrique doit être affichée selon le mode
  const isMetricVisibleInMode = (metricKey) => {
    if (metricsMode === 'engagement') {
      return engagementMetrics.includes(metricKey);
    }
    return performanceMetrics.includes(metricKey) && isMetricSelected(metricKey);
  };

  // Charger les données vidéos Performance Max
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) {
      return;
    }

    const loadPerformanceMaxVideos = async () => {
      try {
        setLoading(true);
        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        console.log('🎬 [PMAX-VIDEOS-API] Début du chargement des vidéos Performance Max');
        console.log('🎬 [PMAX-VIDEOS-API] Request body:', requestBody);

        const response = await axios.post(
          `${config.API_URL}${apiEndpoint}`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('🎬 [PMAX-VIDEOS-API] Réponse reçue:', response.data);

        if (response.data.success) {
          const videos = response.data.data.videos || [];
          console.log('🎬 [PMAX-VIDEOS-API] Vidéos trouvées:', videos.length);
          console.log('🎬 [PMAX-VIDEOS-API] Première vidéo:', videos[0]);
          console.log('🎬 [PMAX-VIDEOS-API] YouTube IDs trouvés:', videos.filter(v => v.youtubeVideoId).length);
          
          // Debug des conversions
          const videosWithConv = videos.filter(v => v.conversions > 0);
          const videosWithConvValue = videos.filter(v => v.conversionsValue > 0);
          console.log('💰 [PMAX-VIDEOS-API] Vidéos avec conversions > 0:', videosWithConv.length);
          console.log('💰 [PMAX-VIDEOS-API] Vidéos avec conversionsValue > 0:', videosWithConvValue.length);
          
          if (videosWithConvValue.length > 0) {
            console.log('💰 [PMAX-VIDEOS-API] Exemple vidéo avec conversionsValue:', {
              title: videosWithConvValue[0].title,
              conversions: videosWithConvValue[0].conversions,
              conversionsValue: videosWithConvValue[0].conversionsValue,
              cost: videosWithConvValue[0].cost
            });
          }
          
          setVideosData(videos);
          setIsDataLoaded(true);
          
          // Envoyer les données au composant parent pour les métriques détaillées
          if (onDataLoaded && typeof onDataLoaded === 'function') {
            onDataLoaded(videos);
          }
        } else {
          console.log('🎬 [PMAX-VIDEOS-API] Erreur dans la réponse:', response.data);
          setError('Erreur lors du chargement des vidéos Performance Max');
        }
      } catch (error) {
        console.log('🎬 [PMAX-VIDEOS-API] Erreur lors de l\'appel API:', error);
        setError('Erreur lors du chargement des vidéos Performance Max');
      } finally {
        setLoading(false);
      }
    };

    loadPerformanceMaxVideos();
  }, [
    selectedAccount, 
    selectedPeriod, 
    customStartDate, 
    customEndDate, 
    selectedCampaignFilter,
    selectedCampaignTypeFilter,
    token, 
    setError
  ]);

  // Filtrage des vidéos (memoized)
  const filteredVideos = useMemo(() => {
    let filtered = [...videosData];
    
    // Filtrer par vidéo sélectionnée si applicable
    if (selectedVideoFilter) {
      filtered = filtered.filter(video => 
        video.id === selectedVideoFilter.id || 
        video.videoId === selectedVideoFilter.id ||
        video.youtubeVideoId === selectedVideoFilter.id ||
        String(video.id) === String(selectedVideoFilter.id) ||
        video.title === selectedVideoFilter.title
      );
    }
    
    // Filtrer par type de source (manuel/automatique)
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(video => {
        if (sourceFilter === 'manual') {
          return video.sourceType === 'MANUAL';
        } else if (sourceFilter === 'auto') {
          return video.sourceType === 'AUTO_GENERATED';
        }
        return true;
      });
    }
    
    return filtered;
  }, [videosData, selectedVideoFilter, sourceFilter]);

  // Fonction de tri memoized
  const sortedVideos = useMemo(() => {
    if (!filteredVideos.length) return [];

    const sorted = [...filteredVideos];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      // Calculs spéciaux pour certaines métriques
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else if (sortConfig.key === 'cpv') {
        aValue = a.views > 0 ? (a.cost / a.views) : Number.MAX_VALUE;
        bValue = b.views > 0 ? (b.cost / b.views) : Number.MAX_VALUE;
      } else if (sortConfig.key === 'vtr') {
        aValue = a.impressions > 0 ? (a.views / a.impressions * 100) : 0;
        bValue = b.impressions > 0 ? (b.views / b.impressions * 100) : 0;
      } else if (sortConfig.key === 'taux_engagement') {
        aValue = a.impressions > 0 ? (a.engagements / a.impressions * 100) : 0;
        bValue = b.impressions > 0 ? (b.engagements / b.impressions * 100) : 0;
      } else if (sortConfig.key === 'title') {
        // Tri par titre : priorité YouTube title > title > name
        aValue = (a.youtubeVideoTitle || a.title || a.name || '').toLowerCase();
        bValue = (b.youtubeVideoTitle || b.title || b.name || '').toLowerCase();
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredVideos, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedVideos.length / itemsPerPage);
  const paginatedVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedVideos.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedVideos, currentPage, itemsPerPage]);

  // Réinitialiser la pagination quand les données changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredVideos]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!filteredVideos.length) return {};

    const baseTotals = filteredVideos.reduce((acc, video) => ({
      impressions: acc.impressions + (video.impressions || 0),
      views: acc.views + (video.views || 0),
      clicks: acc.clicks + (video.clicks || 0),
      cost: acc.cost + (video.cost || 0),
      conversions: acc.conversions + (video.conversions || 0),
      conversionsValue: acc.conversionsValue + (video.conversionsValue || 0),
      engagements: acc.engagements + (video.engagements || 0)
    }), { impressions: 0, views: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, engagements: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpv: baseTotals.views > 0 ? (baseTotals.cost / baseTotals.views) : 0,
      cpm: baseTotals.impressions > 0 ? (baseTotals.cost / baseTotals.impressions * 1000) : 0,
      vtr: baseTotals.impressions > 0 ? (baseTotals.views / baseTotals.impressions * 100) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      taux_engagement: baseTotals.impressions > 0 ? (baseTotals.engagements / baseTotals.impressions * 100) : 0
    };
  }, [filteredVideos]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Ne pas afficher le composant si pas de données et pas en loading
  if (!loading && isDataLoaded && filteredVideos.length === 0) {
    return null;
  }
  
  if (!loading && !isDataLoaded && !selectedAccount) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Video size={16} />&nbsp;Vidéos Performance Max</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Sélecteur de mode métriques - masqué */}
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Mode :</span>
            <select
              value={metricsMode}
              onChange={(e) => setMetricsMode(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.75rem',
                backgroundColor: 'white'
              }}
            >
              <option value="performance">Performance</option>
              <option value="engagement">Engagement</option>
            </select>
          </div>
          
          {/* Filtre par type de source */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Vidéos :</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.75rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">Toutes</option>
              <option value="manual">👤 Manuelles</option>
              <option value="auto">🤖 Automatiques</option>
            </select>
          </div>
          <button 
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {isVisible && (
        <div className="table-container">
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des vidéos Performance Max...<br /><br />
            </div>
          ) : !isDataLoaded ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Video size={16} /> Vidéos Performance Max</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  Sélectionnez un compte et une période pour voir les performances vidéos.
                </p>
              </div>
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Video size={16} /> Aucune vidéo trouvée</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  Aucune vidéo Performance Max trouvée pour la période sélectionnée.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Tableau des vidéos */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('title')} className="sortable">
                        Vidéo {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isMetricVisibleInMode('impressions') && (
                        <th onClick={() => handleSort('impressions')} className="sortable numeric">
                          Impr. {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {metricsMode === 'engagement' && (
                        <>
                          <th onClick={() => handleSort('views')} className="sortable numeric">
                            Vues {sortConfig.key === 'views' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleSort('vtr')} className="sortable numeric">
                            VTR {sortConfig.key === 'vtr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleSort('cpv')} className="sortable numeric">
                            CPV {sortConfig.key === 'cpv' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleSort('cpm')} className="sortable numeric">
                            CPM {sortConfig.key === 'cpm' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleSort('engagements')} className="sortable numeric">
                            Engagements {sortConfig.key === 'engagements' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleSort('taux_engagement')} className="sortable numeric">
                            Taux engagement {sortConfig.key === 'taux_engagement' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                        </>
                      )}
                      {metricsMode === 'performance' && (
                        <>
                          {isMetricSelected('ctr') && (
                            <th onClick={() => handleSort('ctr')} className="sortable numeric">
                              CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('clics') && (
                            <th onClick={() => handleSort('clicks')} className="sortable numeric">
                              Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('cout') && (
                            <th onClick={() => handleSort('cost')} className="sortable numeric">
                              Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('cpc') && (
                            <th onClick={() => handleSort('cpc')} className="sortable numeric">
                              CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('conv') && (
                            <th onClick={() => handleSort('conversions')} className="sortable numeric">
                              Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('taux_conv') && (
                            <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                              Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('cpa') && (
                            <th onClick={() => handleSort('cpa')} className="sortable numeric">
                              CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('valeur_conv') && (
                            <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                              Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMetricSelected('cos') && (
                            <th onClick={() => handleSort('cos')} className="sortable numeric">
                              COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedVideos.map((video, index) => {
                      // Calculs des métriques dérivées pour cette ligne
                      const ctr = video.impressions > 0 ? (video.clicks / video.impressions * 100) : 0;
                      const cpc = video.clicks > 0 ? (video.cost / video.clicks) : 0;
                      const cpv = video.views > 0 ? (video.cost / video.views) : 0;
                      const cpm = video.impressions > 0 ? (video.cost / video.impressions * 1000) : 0;
                      const vtr = video.impressions > 0 ? (video.views / video.impressions * 100) : 0;
                      const conversionRate = video.clicks > 0 ? (video.conversions / video.clicks * 100) : 0;
                      const cpa = video.conversions > 0 ? (video.cost / video.conversions) : 0;
                      const cos = video.conversionsValue > 0 ? (video.cost / video.conversionsValue * 100) : 0;
                      const tauxEngagement = video.impressions > 0 ? (video.engagements / video.impressions * 100) : 0;

                      return (
                        <tr key={video.id || index}>
                          <td style={{ maxWidth: '300px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {video.youtubeVideoId && (
                                <a 
                                  href={`https://www.youtube.com/watch?v=${video.youtubeVideoId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Ouvrir la vidéo sur YouTube"
                                >
                                  <img 
                                    src={`https://img.youtube.com/vi/${video.youtubeVideoId}/mqdefault.jpg`}
                                    alt="Miniature vidéo"
                                    style={{ 
                                      width: '80px', 
                                      height: '45px', 
                                      borderRadius: '4px', 
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      transition: 'opacity 0.2s',
                                      opacity: '1',
                                      border: '1px solid #ddd'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    onError={(e) => {
                                      // Fallback vers une image par défaut si la miniature n'existe pas
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iNDUiIHZpZXdCb3g9IjAgMCA4MCA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjQ1IiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zMiAyMUwzOCAyNUwzMiAyOVYyMVoiIGZpbGw9IiM5OTk5OTkiLz4KPC9zdmc+';
                                    }}
                                  />
                                </a>
                              )}
                              <div style={{ overflow: 'hidden' }}>
                                <div style={{ 
                                  fontWeight: '500', 
                                  fontSize: '0.875rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  {/* Indicateur de type de vidéo */}
                                  <span 
                                    style={{
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      borderRadius: '8px',
                                      fontWeight: '600',
                                      backgroundColor: video.sourceType === 'MANUAL' ? '#dcfce7' : '#f3f4f6',
                                      color: video.sourceType === 'MANUAL' ? '#166534' : '#6b7280',
                                      border: `1px solid ${video.sourceType === 'MANUAL' ? '#bbf7d0' : '#e5e7eb'}`
                                    }}
                                    title={video.sourceType === 'MANUAL' ? 'Vidéo ajoutée manuellement' : 'Vidéo générée automatiquement par Google Ads'}
                                  >
                                    {video.sourceType === 'MANUAL' ? '👤 Manuel' : '🤖 Auto'}
                                  </span>
                                  
                                  {video.youtubeVideoId ? (
                                    <a 
                                      href={`https://www.youtube.com/watch?v=${video.youtubeVideoId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        color: '#1f2937',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                      title={`Titre complet: ${video.youtubeVideoTitle || video.title || video.name || 'Titre non disponible'}\n\nCliquer pour ouvrir sur YouTube`}
                                    >
                                      {video.youtubeVideoTitle || video.title || video.name || `Vidéo ${video.id}`}
                                    </a>
                                  ) : (
                                    <span 
                                      style={{ 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                      }}
                                      title={`Titre complet: ${video.youtubeVideoTitle || video.title || video.name || 'Titre non disponible'}`}
                                    >
                                      {video.youtubeVideoTitle || video.title || video.name || `Vidéo ${video.id}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          {isMetricVisibleInMode('impressions') && (
                            <td className="numeric">{formatNumber(video.impressions || 0)}</td>
                          )}
                          {metricsMode === 'engagement' && (
                            <>
                              <td className="numeric">{formatNumber(video.views || 0)}</td>
                              <td className="numeric">{formatNumber(vtr, 2)}%</td>
                              <td className="numeric">€{formatNumber(cpv, 3)}</td>
                              <td className="numeric">€{formatNumber(cpm, 2)}</td>
                              <td className="numeric">{formatNumber(video.engagements || 0)}</td>
                              <td className="numeric">{formatNumber(tauxEngagement, 2)}%</td>
                            </>
                          )}
                          {metricsMode === 'performance' && (
                            <>
                              {isMetricSelected('ctr') && (
                                <td className="numeric">{formatNumber(ctr, 2)}%</td>
                              )}
                              {isMetricSelected('clics') && (
                                <td className="numeric">{formatNumber(video.clicks || 0)}</td>
                              )}
                              {isMetricSelected('cout') && (
                                <td className="numeric">€{formatNumber(video.cost || 0, 2)}</td>
                              )}
                              {isMetricSelected('cpc') && (
                                <td className="numeric">€{formatNumber(cpc, 2)}</td>
                              )}
                              {isMetricSelected('conv') && (
                                <td className="numeric">{formatNumber(video.conversions || 0, 1)}</td>
                              )}
                              {isMetricSelected('taux_conv') && (
                                <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                              )}
                              {isMetricSelected('cpa') && (
                                <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', moduleId)}`}>
                                  {cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                                </td>
                              )}
                              {isMetricSelected('valeur_conv') && (
                                <td className="numeric">€{formatNumber(video.conversionsValue || 0, 2)}</td>
                              )}
                              {isMetricSelected('cos') && (
                                <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', moduleId)}`}>
                                  {cos >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>

                  {sortedVideos.length > 0 && (
                    <tfoot>
                      <tr className="total-row">
                        <td style={{ fontWeight: '700' }}>TOTAL</td>
                        {isMetricVisibleInMode('impressions') && (
                          <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                        )}
                        {metricsMode === 'engagement' && (
                          <>
                            <td className="numeric"><strong>{formatNumber(totals.views)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.vtr, 2)}%</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cpv, 3)}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cpm, 2)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.engagements)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.taux_engagement, 2)}%</strong></td>
                          </>
                        )}
                        {metricsMode === 'performance' && (
                          <>
                            {isMetricSelected('ctr') && (
                              <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                            )}
                            {isMetricSelected('clics') && (
                              <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                            )}
                            {isMetricSelected('cout') && (
                              <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                            )}
                            {isMetricSelected('cpc') && (
                              <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                            )}
                            {isMetricSelected('conv') && (
                              <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                            )}
                            {isMetricSelected('taux_conv') && (
                              <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                            )}
                            {isMetricSelected('cpa') && (
                              <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                                <strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong>
                              </td>
                            )}
                            {isMetricSelected('valeur_conv') && (
                              <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                            )}
                            {isMetricSelected('cos') && (
                              <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                                <strong>{totals.cos >= 0 ? `${formatNumber(totals.cos, 1)}%` : '-'}</strong>
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedVideos.length)} sur {sortedVideos.length} vidéos
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

PerformanceMaxVideosTable.displayName = 'PerformanceMaxVideosTable';

export default PerformanceMaxVideosTable;